import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { computed } from 'mobx';
import MainBody from './Components/Body';
import { Helmet } from 'react-helmet';
import infiniteP from './tools/auto/infinite_practice.js';
import IntroVideoModal from './Components/IntroVideoModal';
import FloatingIntroVideo from './Components/FloatingIntroVideo';
import IntroVideoService from './api/IntroVideoService';

import { observer, inject } from 'mobx-react';

@inject('store')
@observer
class Body extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showIntroModal: false,
      showFloatingVideo: false,
      hasWatchedIntro: null,
      checkingIntroStatus: true,
      hasShownFloatingVideo: false,
    };
  }

  async componentDidMount() {
    await this.checkIntroVideoStatus();
  }

  async checkIntroVideoStatus() {
    try {
      const organizationName =
        this.props.store.profile.organization || 'RicoCorp';
      const response =
        await IntroVideoService.checkIntroVideoStatus(organizationName);

      this.setState({
        hasWatchedIntro: response.has_watched_intro,
        checkingIntroStatus: false,
      });

      if (response.has_watched_intro) {
        // User has watched intro, show floating video only if not shown in this session
        const hasShownInSession = sessionStorage.getItem('floatingVideoShown');
        if (!hasShownInSession) {
          this.setState({ showFloatingVideo: true });
          sessionStorage.setItem('floatingVideoShown', 'true');
        }
      } else {
        // User hasn't watched intro, show mandatory modal
        this.setState({ showIntroModal: true });
      }
    } catch (error) {
      console.error('Error checking intro video status:', error);
      // Don't block user access if intro video check fails
      // Allow them to continue to the platform
      this.setState({ 
        checkingIntroStatus: false,
        hasWatchedIntro: true, // Assume watched to avoid blocking access
        showIntroModal: false,
        showFloatingVideo: false
      });
    }
  }

  handleIntroVideoCompleted = () => {
    this.setState({
      showIntroModal: false,
      hasWatchedIntro: true,
      showFloatingVideo: true,
    });
    // Mark floating video as shown for this session
    sessionStorage.setItem('floatingVideoShown', 'true');
  };

  handleFloatingVideoClose = () => {
    this.setState({ showFloatingVideo: false });
  };

  @computed get permissions() {
    return this.props.store.tools;
  }

  @computed get autoescuela() {
    return this.permissions.filter(tool => tool.category === 'Instructor IA');
  }

  @computed get admin() {
    return this.permissions.filter(tool => tool.category === 'Administración');
  }

  // Updated: Use the injected store directly for orgSettings
  @computed get orgSettings() {
    return this.props.store.orgSettings || {};
  }

  render() {
    // Retrieve module settings; if not loaded, use empty objects.
    const modulos = this.orgSettings.modulos || {};
    const teoriaPermisoB = modulos.TeoriaPermisoB || {};
    // Check if the main Permiso B module is enabled
    const permisoBEnabled = teoriaPermisoB.enabled ?? false;
    // Check which child modules are enabled
    const videosEnabled = teoriaPermisoB?.children?.Videos?.enabled ?? false;
    const practicaInfinitaEnabled =
      teoriaPermisoB?.children?.PracticaInfinita?.enabled ?? false;
    const simuladorExamenesEnabled =
      teoriaPermisoB?.children?.SimuladorExamenes?.enabled ?? false;
    const podcastsEnabled =
      teoriaPermisoB?.children?.Podcasts?.enabled ?? false;

    // Practicas settings
    const practicas = modulos.Practicas || {};
    const practicasEnabled = practicas.enabled ?? false;
    const sesionesConduccionEnabled =
      practicas?.children?.SesionesConduccion?.enabled ?? false;

    return (
      <>
        <Helmet>
          <title>{'Auto'}</title>
        </Helmet>
        <MainBody className="px-4 py-4 md:px-28 md:py-8 lg:py-12 ">
          {/* Render the Permiso B section only if enabled in org settings */}
          {permisoBEnabled ? (
            <>
              <Title title="Permiso B" />
              <Grid>
                {videosEnabled && (
                  <Tool
                    group="Teórica"
                    title="Videos"
                    to="/auto/videos"
                    Icon={infiniteP.Icon}
                    desc="Galeria de videos explicativos sobre cada tema del Permiso B."
                    fromColor="yellow-400"
                    toColor={infiniteP.toColor}
                  />
                )}
                {practicaInfinitaEnabled && (
                  <Tool
                    group="Teórica"
                    title={infiniteP.title}
                    to={infiniteP.to}
                    Icon={infiniteP.Icon}
                    desc={infiniteP.desc}
                    fromColor={infiniteP.fromColor}
                    toColor={infiniteP.toColor}
                  />
                )}
                {simuladorExamenesEnabled && (
                  <Tool
                    group="Teórica"
                    title="Simulador de Exámenes"
                    to="/auto/examen"
                    Icon={infiniteP.Icon}
                    desc="Simulador de exámenes de la DGT con el que podrás practicar todo lo que necesites antes de presentarte al examen oficial."
                    fromColor={infiniteP.fromColor}
                    toColor={infiniteP.toColor}
                  />
                )}
                {podcastsEnabled && (
                  <Tool
                    group="Teórica"
                    title="Podcasts"
                    to="/auto/podcasts"
                    Icon={infiniteP.Icon}
                    desc="Accede a tus Podcasts personalizados según tu progreso y diseñados para reforzar exactamente lo que necesitas."
                    fromColor={infiniteP.fromColor}
                    toColor={infiniteP.toColor}
                  />
                )}
                {/*
                  Uncomment the code below if you wish to map over the autoescuela tools in the future:
                  {this.autoescuela.map((tool, index) => (
                    <Tool
                      key={index}
                      group={tool.category}
                      title={tool.title}
                      to={tool.to}
                      Icon={tool.Icon}
                      desc={tool.desc}
                      fromColor={tool.fromColor}
                      toColor={tool.toColor}
                    />
                  ))}
                */}
              </Grid>
              <Divider />
              <Grid>
                {practicasEnabled && sesionesConduccionEnabled && (
                  <Tool
                    group="Prácticas"
                    title="Sesiones de Conducción"
                    to="/auto/driving"
                    Icon={infiniteP.Icon}
                    desc="Accede a tus sesiones de conducción."
                    fromColor="green-400"
                    toColor={infiniteP.toColor}
                  />
                )}
              </Grid>
            </>
          ) : null}
        </MainBody>

        {/* Intro Video Components */}
        <IntroVideoModal
          show={this.state.showIntroModal}
          onVideoCompleted={this.handleIntroVideoCompleted}
          organizationName={this.props.store.profile.organization || 'RicoCorp'}
        />

        <FloatingIntroVideo
          show={this.state.showFloatingVideo}
          onClose={this.handleFloatingVideoClose}
        />
      </>
    );
  }
}

export const Divider = () => (
  <div className="divide-y-2 divide-dashed divide-gray-300 py-8 md:py-12">
    {' '}
    <div></div>
    <div></div>
  </div>
);

export const Title = ({ title }) => (
  <h2 className="text-xl sm:text-2xl md:text-3xl text-gray-700 mb-4 md:mb-6">
    {title}
  </h2>
);

export const Grid = ({ children }) => (
  <div className="grid grid-cols-1 gap-8 mt-4 lg:grid-cols-2 xl:grid-cols-3 ">
    {children}
  </div>
);

export const Tool = ({ Icon, title, desc, to, group, fromColor, toColor }) => (
  <Link to={to || '/'} className="flex relative ">
    <div
      className={`bg-white flex-1 rounded-xl transition hover:shadow-md overflow-hidden md:max-w-1lg text-gray-500 cursor-pointer border border-gray-300 md:flex relative transform hover:scale-105 hover:text-black`}
    >
      <div className="p-4">
        <div
          className={`uppercase tracking-wide text-sm text-${
            fromColor ? fromColor : 'green-500'
          } font-semibold leading-none`}
        >
          {group || 'New'}
        </div>
        <div className="block text-lg xl:text-xl 2xl:text-2xl leading-tight font-medium text-black leading-none">
          {title}
        </div>
        <p className="mt-1 pr-1 text-sm ">{desc} </p>
      </div>
    </div>
  </Link>
);

export default Body;
